// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — Solar System mode in the browser: the orrery drawn on a canvas
 * over the globe (docs/AYNI_XR_SOLAR_SYSTEM_PLAN.md). The scene itself is
 * shared with the headset (services/solarSystem/solarSystemScene.ts); this
 * file owns the canvas, the camera (drag to turn round the Sun, wheel or
 * pinch to come closer), the pointer, the fades and the flight's timing.
 *
 * The canvas sits between the globes (`#map-grid`) and the controls
 * (`#ui`), so every button stays where it is. A fade is a black layer in
 * the same place: it covers the globe, never the controls.
 */

import type * as THREE from 'three'
import { createVrNightSky, type VrNightSkyHandle } from '../services/vrNightSky'
import { createSolarSystemScene, type SolarSceneHandle } from '../services/solarSystem/solarSystemScene'
import type { SolarBodyId } from '../services/solarSystem/bodies'
import type { OrreryLayout } from '../services/solarSystem/layout'
import { flightSeconds } from '../services/solarSystem/flight'

/** A fade's half: out to black, or in from it, ms. */
export const FADE_MS = 450
/** The canvas fading off the globe after an arrival, ms. */
const REVEAL_MS = 550

export interface SolarViewOptions {
  container: HTMLElement
  /** Before this element (the controls), so they stay on top. */
  before: Element | null
  nameOf: (id: SolarBodyId) => string
  pictureUrl: (id: SolarBodyId) => string | null
  /** A body was clicked or tapped (not dragged). */
  onPick: (id: SolarBodyId) => void
  /** The clock moved (each frame while it plays). */
  onTime?: (ms: number) => void
}

export interface SolarViewFlight {
  from: SolarBodyId
  to: SolarBodyId
  /** The globe on screen, CSS pixels in the container: where the arriving body lands and how big. */
  globe: { cx: number; cy: number; radiusPx: number }
}

export interface SolarViewHandle {
  readonly scene: SolarSceneHandle
  readonly canvas: HTMLCanvasElement
  readonly visible: boolean
  readonly layout: OrreryLayout
  readonly timeMs: number
  readonly playing: boolean
  /** Days of orrery time a second of ours. */
  readonly rate: number
  show(): void
  hide(): void
  setLayout(layout: OrreryLayout): void
  setTime(ms: number): void
  setPlaying(on: boolean): void
  setRate(daysPerSecond: number): void
  /** Back to the starting view: Pluto's orbit fits, seen from above at a slant. */
  resetCamera(): void
  /** Black over the globe (true) or none (false), resolved when done. */
  fade(toBlack: boolean, ms?: number): Promise<void>
  /**
   * The flight: the canvas shows the trip from `from` to `to` (the black
   * layer is lifted at its start), and resolves on arrival with the true
   * distance and the time it took. The canvas stays up, showing the
   * arriving body where the globe will be, until {@link reveal}.
   */
  fly(f: SolarViewFlight): Promise<{ distanceKm: number; seconds: number }>
  /** Fades the canvas off the globe (after a flight), then hides it. */
  reveal(): Promise<void>
  /** Screen position of a body (CSS px in the container), for checks. */
  screenOf(id: SolarBodyId): { x: number; y: number } | null
  relabel(): void
  dispose(): void
}

export async function createSolarSystemView(THREE_: typeof THREE, opts: SolarViewOptions): Promise<SolarViewHandle> {
  const { container } = opts
  const canvas = document.createElement('canvas')
  canvas.id = 'solar-system-canvas'
  canvas.className = 'solar-system-canvas'
  canvas.hidden = true
  canvas.setAttribute('aria-hidden', 'true')
  const fadeEl = document.createElement('div')
  fadeEl.id = 'solar-system-fade'
  fadeEl.className = 'solar-system-fade'
  fadeEl.setAttribute('aria-hidden', 'true')
  container.insertBefore(canvas, opts.before)
  container.insertBefore(fadeEl, opts.before)

  const renderer = new THREE_.WebGLRenderer({ canvas, antialias: true, alpha: false, preserveDrawingBuffer: true })
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
  renderer.outputColorSpace = THREE_.SRGBColorSpace
  const scene3 = new THREE_.Scene()
  scene3.background = new THREE_.Color(0x000000)
  const camera = new THREE_.PerspectiveCamera(45, 1, 0.01, 200)
  const skyHolder = new THREE_.Group()
  scene3.add(skyHolder)
  const sky: VrNightSkyHandle = createVrNightSky(THREE_, skyHolder, { sun: false })
  const scene = createSolarSystemScene(THREE_, { nameOf: opts.nameOf, pictureUrl: opts.pictureUrl })
  scene3.add(scene.root)
  scene.setPlacement(new THREE_.Vector3(), new THREE_.Quaternion(), 1)

  // The orbit camera round the Sun.
  const orbit = { dist: 7, az: -0.5, el: 0.95, target: new THREE_.Vector3() }
  let visible = false
  let playing = false
  let rate = 1
  let timeMs = scene.timeMs
  let raf = 0
  let last = 0
  let flight: null | { started: number; seconds: number; resolve: (v: { distanceKm: number; seconds: number }) => void; distanceKm: number } = null
  let fitted = false

  function size(): { w: number; h: number } {
    const r = container.getBoundingClientRect()
    return { w: Math.max(1, Math.round(r.width)), h: Math.max(1, Math.round(r.height)) }
  }

  function fitDistance(): number {
    const { w, h } = size()
    const vfov = (camera.fov * Math.PI) / 180
    const hfov = 2 * Math.atan(Math.tan(vfov / 2) * (w / h))
    // Pluto's orbit (2.5 m out, 17° tilted) with a margin, seen at the starting slant.
    return 2.75 / Math.sin(Math.min(vfov, hfov) / 2)
  }

  function resize(): void {
    const { w, h } = size()
    renderer.setSize(w, h, false)
    canvas.style.width = `${w}px`
    canvas.style.height = `${h}px`
    camera.aspect = w / h
    camera.updateProjectionMatrix()
  }

  function placeCamera(): void {
    if (flight) return
    const { dist, az, el, target } = orbit
    camera.clearViewOffset()
    camera.position.set(
      target.x + dist * Math.cos(el) * Math.sin(az),
      target.y + dist * Math.sin(el),
      target.z + dist * Math.cos(el) * Math.cos(az),
    )
    camera.up.set(0, 1, 0)
    camera.lookAt(target)
    camera.near = Math.max(0.0005, dist * 0.004)
    camera.far = 200
    camera.updateProjectionMatrix()
  }

  function pixelsPerRadian(): number {
    const { h } = size()
    return h / 2 / Math.tan((camera.fov * Math.PI) / 360)
  }

  function frame(now: number): void {
    raf = requestAnimationFrame(frame)
    // Wall-clock time (capped at half a second after a stall), so a slow device runs the same animations, only in fewer frames.
    const dt = last ? Math.min(0.5, (now - last) / 1000) : 0
    last = now
    if (playing && !flight) {
      const next = timeMs + dt * rate * 86_400_000
      scene.setTime(next)
      timeMs = scene.timeMs
      if (timeMs !== next) playing = false
      opts.onTime?.(timeMs)
    }
    if (flight) {
      const u = Math.min(1, (now - flight.started) / 1000 / flight.seconds)
      scene.setFlightProgress(u)
      if (u >= 1) {
        const done = flight
        flight = null
        done.resolve({ distanceKm: done.distanceKm, seconds: done.seconds })
      }
    } else {
      placeCamera()
    }
    camera.updateMatrixWorld()
    skyHolder.position.copy(camera.position)
    scene.update(camera.position, pixelsPerRadian(), dt)
    sky.update(true, scene.celestialToWorld(), timeMs, size().h)
    renderer.render(scene3, camera)
  }

  function start(): void {
    if (raf) return
    last = 0
    raf = requestAnimationFrame(frame)
  }

  function stop(): void {
    if (raf) cancelAnimationFrame(raf)
    raf = 0
  }

  // ---------------------------------------------------------------- pointer

  const pointers = new Map<number, { x: number; y: number }>()
  let downAt: { x: number; y: number; t: number } | null = null
  let moved = false
  let pinch0 = 0
  let dist0 = 0
  const raycaster = new THREE_.Raycaster()

  function rayAt(clientX: number, clientY: number): { origin: THREE.Vector3; dir: THREE.Vector3 } {
    const r = canvas.getBoundingClientRect()
    const ndc = new THREE_.Vector2(((clientX - r.left) / r.width) * 2 - 1, -((clientY - r.top) / r.height) * 2 + 1)
    raycaster.setFromCamera(ndc, camera)
    return { origin: raycaster.ray.origin.clone(), dir: raycaster.ray.direction.clone() }
  }

  function pickAt(clientX: number, clientY: number, touch: boolean): SolarBodyId | null {
    const { origin, dir } = rayAt(clientX, clientY)
    return scene.pick(origin, dir, (touch ? 22 : 14) / pixelsPerRadian())
  }

  canvas.addEventListener('pointerdown', (e) => {
    if (flight) return
    canvas.setPointerCapture(e.pointerId)
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pointers.size === 1) {
      downAt = { x: e.clientX, y: e.clientY, t: performance.now() }
      moved = false
    } else if (pointers.size === 2) {
      const [a, b] = [...pointers.values()]
      pinch0 = Math.hypot(a.x - b.x, a.y - b.y)
      dist0 = orbit.dist
      moved = true
    }
  })
  canvas.addEventListener('pointermove', (e) => {
    if (flight) return
    const prev = pointers.get(e.pointerId)
    if (!prev) {
      const id = pickAt(e.clientX, e.clientY, false)
      scene.setHover(id)
      canvas.style.cursor = id ? 'pointer' : 'grab'
      return
    }
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pointers.size === 2) {
      const [a, b] = [...pointers.values()]
      const d = Math.hypot(a.x - b.x, a.y - b.y)
      if (pinch0 > 0) orbit.dist = clampDist((dist0 * pinch0) / Math.max(1, d))
      return
    }
    const dx = e.clientX - prev.x
    const dy = e.clientY - prev.y
    if (downAt && Math.hypot(e.clientX - downAt.x, e.clientY - downAt.y) > 5) moved = true
    if (moved) {
      canvas.style.cursor = 'grabbing'
      orbit.az -= dx * 0.006
      orbit.el = Math.min(1.53, Math.max(-1.2, orbit.el + dy * 0.006))
    }
  })
  const endPointer = (e: PointerEvent): void => {
    const had = pointers.delete(e.pointerId)
    if (!had) return
    if (pointers.size === 0) {
      canvas.style.cursor = 'grab'
      if (!moved && downAt && performance.now() - downAt.t < 600 && !flight) {
        const id = pickAt(e.clientX, e.clientY, e.pointerType !== 'mouse')
        if (id) opts.onPick(id)
      }
      downAt = null
      pinch0 = 0
    }
  }
  canvas.addEventListener('pointerup', endPointer)
  canvas.addEventListener('pointercancel', endPointer)
  canvas.addEventListener('pointerleave', () => scene.setHover(null))
  canvas.addEventListener(
    'wheel',
    (e) => {
      if (flight) return
      e.preventDefault()
      orbit.dist = clampDist(orbit.dist * Math.exp(e.deltaY * 0.0012))
    },
    { passive: false },
  )

  function clampDist(d: number): number {
    return Math.min(30, Math.max(0.04, d))
  }

  const ro = new ResizeObserver(() => {
    if (!visible) return
    resize()
    if (!fitted) handle.resetCamera()
  })
  ro.observe(container)

  // ---------------------------------------------------------------- handle

  const handle: SolarViewHandle = {
    scene,
    canvas,
    get visible() {
      return visible
    },
    get layout() {
      return scene.layout
    },
    get timeMs() {
      return timeMs
    },
    get playing() {
      return playing
    },
    get rate() {
      return rate
    },

    show() {
      visible = true
      canvas.hidden = false
      canvas.style.opacity = '1'
      canvas.style.transition = ''
      resize()
      if (!fitted) handle.resetCamera()
      start()
    },

    hide() {
      visible = false
      canvas.hidden = true
      playing = false
      stop()
    },

    setLayout(layout) {
      scene.setLayout(layout, true)
    },

    setTime(ms) {
      scene.setTime(ms)
      timeMs = scene.timeMs
      opts.onTime?.(timeMs)
    },

    setPlaying(on) {
      playing = on
    },

    setRate(days) {
      rate = days
    },

    resetCamera() {
      orbit.target.set(0, 0, 0)
      orbit.az = -0.5
      orbit.el = 0.95
      orbit.dist = fitDistance()
      fitted = true
      placeCamera()
    },

    fade(toBlack, ms = FADE_MS) {
      return new Promise((resolve) => {
        fadeEl.style.transition = `opacity ${ms}ms ease`
        // Reading a layout value commits the start state, so the transition runs.
        void fadeEl.offsetWidth
        fadeEl.classList.toggle('on', toBlack)
        window.setTimeout(resolve, ms + 30)
      })
    },

    fly(f) {
      resize()
      const { w, h } = size()
      // The camera at the origin looking down −z, its centre moved onto the globe's.
      camera.position.set(0, 0, 0)
      camera.up.set(0, 1, 0)
      camera.lookAt(0, 0, -1)
      camera.near = 0.01
      camera.far = 200
      // The globe's place is given in the container; the canvas may stand slid aside with it.
      const cr = canvas.getBoundingClientRect()
      const br = container.getBoundingClientRect()
      const gx = f.globe.cx - (cr.left - br.left)
      const gy = f.globe.cy - (cr.top - br.top)
      camera.setViewOffset(w, h, -(gx - w / 2), -(gy - h / 2), w, h)
      camera.updateProjectionMatrix()
      camera.updateMatrixWorld()
      const d = 3
      const angular = Math.min(1.2, f.globe.radiusPx / pixelsPerRadian())
      const radius = d * Math.sin(angular)
      const distanceKm = scene.beginFlight({
        from: f.from,
        to: f.to,
        timeMs,
        viewer: new THREE_.Vector3(0, 0, 0),
        ahead: new THREE_.Vector3(0, 0, -1),
        up: new THREE_.Vector3(0, 1, 0),
        arrivalDistanceM: d,
        arrivalRadiusM: radius,
      })
      const seconds = flightSeconds(distanceKm)
      playing = false
      visible = true
      canvas.hidden = false
      canvas.style.transition = ''
      canvas.style.opacity = '1'
      start()
      return new Promise((resolve) => {
        flight = { started: performance.now() + FADE_MS * 0.4, seconds, resolve, distanceKm }
        void handle.fade(false)
      })
    },

    reveal() {
      return new Promise((resolve) => {
        canvas.style.transition = `opacity ${REVEAL_MS}ms ease`
        void canvas.offsetWidth
        canvas.style.opacity = '0'
        window.setTimeout(() => {
          scene.endFlight()
          handle.hide()
          canvas.style.opacity = '1'
          canvas.style.transition = ''
          resolve()
        }, REVEAL_MS + 30)
      })
    },

    screenOf(id) {
      const p = scene.bodyWorldPosition(id, new THREE_.Vector3()).project(camera)
      if (p.z > 1) return null
      const { w, h } = size()
      return { x: ((p.x + 1) / 2) * w, y: ((1 - p.y) / 2) * h }
    },

    relabel() {
      scene.relabel()
    },

    dispose() {
      stop()
      ro.disconnect()
      sky.dispose()
      scene.dispose()
      renderer.dispose()
      canvas.remove()
      fadeEl.remove()
    },
  }
  return handle
}
